import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Control, FieldPath, useForm } from 'react-hook-form';
import { DevTool } from '@hookform/devtools';
import { zodResolver } from '@hookform/resolvers/zod';
import useMeasure from 'react-use-measure';
import {
  useEventMerchantOrderSharedLinkInfoLazyQuery,
  EventMerchantOrderSharedLinkInfoQuery,
} from '@/graphql/queries/eventMerchantOrderSharedLinkInfo.generated';
import {
  ApolloClient,
  ApolloError,
  InMemoryCache,
  HttpLink,
} from '@apollo/client';
import { getGlobalContext } from '@/utils/window';
import {
  getContainerOrderFromRemoteFormData,
  getHasOrderChanged,
  safeReset,
} from './utils';
import { defaultFormValues, orderFormSchema, OrderFormValues } from './schema';
import { EventOrderBottomBar } from './components/EventOrderBottomBar';
import { useSubmitEventMerchantOrderMutation } from '@/graphql/mutations/submitEventMerchantOrder.generated';
import {
  ItemTypeCategory,
  MerchantOrderItemUnit,
  SubmitEventMerchantOrderItemInput,
} from '@/graphql/generatedTypes';
import { flushSync } from 'react-dom';
import { isValidPhoneNumber } from 'react-phone-number-input';
import i18n from '@/utils/i18n';

const DEFAULT_TAX_PERCENTAGE = 19;
const DEFAULT_CURRENCY = 'EUR';

// colugo's ErrorCodes.BILLING_ADDRESS_INVALID: the geocoder cannot find the
// billing address, or a part of it is missing.
const BILLING_ADDRESS_INVALID = 'BILLING_ADDRESS_INVALID';
const BILLING_ADDRESS_FIELDS: FieldPath<OrderFormValues>[] = [
  'address.street',
  'address.zip',
  'address.city',
];

// Shared with the confirmation cards so each label can be bound to its checkbox.
export const CONFIRMATION_CHECKBOX_IDS = {
  additionalInfo: 'confirm-event-specific-terms',
  termsAndConditions: 'confirm-general-terms',
} as const;

type ConfirmationKey = keyof typeof CONFIRMATION_CHECKBOX_IDS;

export type FormLoadingStatus =
  | 'initializing' // Before first fetch
  | 'loading' // Fetching data
  | 'ready' // Data loaded, form ready
  | 'submitting' // Submitting form
  | 'fetch-error' // Error fetching initial data
  | 'submit-error' // Error submitting form
  | 'invalid-link'; // Invalid shared link (404)

export type ContainerEntry = {
  info: {
    id: number;
    name: string;
    imageUrl: string | null | undefined;
    segment: string;
    packingUnit: number;
    sizeHelper: string;
    color: string | null | undefined;
  };
  quantity: number;
  unit: MerchantOrderItemUnit;
  price: {
    cleaningPrice: number;
    depositPrice: number;
    lostPrice: number;
    rentalPrice: number;
  };
};
/**
 * A container station line, from the event's allowed_item_types narrowed by
 * ORDER_FORM_ITEM_TYPE_CATEGORIES. Counted in pieces rather than boxes.
 */
export type ItemTypeEntry = {
  info: {
    id: number;
    name: string;
    imageUrl: string | null | undefined;
    color: string | null | undefined;
    category: ItemTypeCategory;
  };
  quantity: number;
  price?: {
    cleaningPrice: number;
    depositPrice: number;
    lostPrice: number;
    rentalPrice: number;
  };
};
type ItemLocationEntry = {
  itemLocationName: string;
  itemLocationId: string;
  containers: {
    [containerTypeId: PropertyKey]: ContainerEntry;
  };
  itemTypes: {
    [itemTypeId: PropertyKey]: ItemTypeEntry;
  };
};
export type ContainerOrder = {
  [itemLocationId: PropertyKey]: ItemLocationEntry;
};

type OrderFormContextValue = {
  hasOrderChanged: boolean;
  formControl: Control<OrderFormValues>;
  currency: string | null;
  formLoadingStatus: FormLoadingStatus;
  errorMessage: string | null;
  taxPercentage: number;
  // Minimum cleaning fee rate in % of rented pcs; null = no minimum,
  // only items returned dirty are billed for cleaning
  minimumCleaningFeeRate: number | null;
  order: ContainerOrder;
  updateOrder: (params: {
    itemLocationId: string;
    containerTypeId: number;
    quantity: number;
  }) => void;
  updateItemTypeOrder: (params: {
    itemLocationId: string;
    itemTypeId: number;
    quantity: number;
  }) => void;
  termsAndConditionsCheckbox: {
    additionalInfo: boolean;
    termsAndConditions: boolean;
  };
  confirmationErrors: Record<ConfirmationKey, boolean>;
  setConfirmation: (key: ConfirmationKey, checked: boolean) => void;
  acceptTermsAndConditions: () => void;
  termsAndConditionsAccepted: boolean;
  submitForm: () => void;
  fetchedFormInfo:
    | EventMerchantOrderSharedLinkInfoQuery['eventMerchantOrderSharedLinkInfo']
    | undefined;
  alreadySubmittedLock: boolean;
  unlockAlreadySubmittedLock: () => void;
  resetForm: () => void;
};

const OrderFormContext = createContext<OrderFormContextValue | undefined>(
  undefined,
);

const apolloUri = localStorage.getItem('pickpack-endpoint');
const httpLink = new HttpLink({ uri: apolloUri ?? '' });
const cache = new InMemoryCache();
const apolloClient = new ApolloClient({
  link: httpLink,
  cache: cache,
  defaultContext: {
    headers: {
      authorization: 'ANONYMOUS',
      'Accept-Language': (getGlobalContext() as any).pickpackLocale || 'de',
      'client-name': 'vytal-admin-web',
    },
  },
});

export function OrderFormProvider({
  sharedLinkId,
  children,
}: {
  sharedLinkId?: string;
  children: React.ReactNode;
}) {
  const [currency, setCurrency] = useState<string>(DEFAULT_CURRENCY);
  const [taxPercentage, setTaxPercentage] = useState<number>(
    DEFAULT_TAX_PERCENTAGE,
  );

  const [formLoadingStatus, setFormLoadingStatus] = useState<FormLoadingStatus>(
    sharedLinkId ? 'initializing' : 'invalid-link',
  );

  const [fetchedFormInfo, setFetchedFormInfo] = useState<
    | EventMerchantOrderSharedLinkInfoQuery['eventMerchantOrderSharedLinkInfo']
    | undefined
  >();

  const [fetchRemoteFormData] = useEventMerchantOrderSharedLinkInfoLazyQuery({
    client: apolloClient,
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  });

  const [submitEventMerchantOrder] = useSubmitEventMerchantOrderMutation({
    client: apolloClient,
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  });

  const [termsAndConditionsCheckbox, setTermsAndConditionsCheckbox] = useState({
    additionalInfo: false,
    termsAndConditions: false,
  });
  const [termsAndConditionsAccepted, setTermsAndConditionsAccepted] =
    useState(false);

  const [confirmationErrors, setConfirmationErrors] = useState({
    additionalInfo: false,
    termsAndConditions: false,
  });

  // Ticking a confirmation clears its own error immediately, so a row the
  // partner has just resolved never keeps complaining.
  const setConfirmation = (key: ConfirmationKey, checked: boolean) => {
    setTermsAndConditionsCheckbox((prev) => ({ ...prev, [key]: checked }));
    if (checked) {
      setConfirmationErrors((prev) => ({ ...prev, [key]: false }));
    }
  };

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [alreadySubmittedLock, setAlreadySubmittedLock] = useState(false);

  const [order, setOrder] = useState<ContainerOrder>({});
  const remoteOrderRef = useRef<ContainerOrder | undefined>(undefined);

  const hasOrderChanged = useMemo(() => {
    return getHasOrderChanged(order, remoteOrderRef.current);
  }, [order]);

  const disabled =
    alreadySubmittedLock ||
    !fetchedFormInfo?.can_submit_or_edit ||
    ['submitting', 'initializing', 'loading'].includes(formLoadingStatus);

  const form = useForm<OrderFormValues>({
    resolver: zodResolver(orderFormSchema),
    defaultValues: defaultFormValues,
    disabled,
    resetOptions: {
      keepSubmitCount: false,
    },
  });

  const applyRemoteFormData = useCallback(
    (
      f: EventMerchantOrderSharedLinkInfoQuery['eventMerchantOrderSharedLinkInfo'],
    ) => {
      let validatedPhoneNumber = f.event_account?.phone_number;

      if (!validatedPhoneNumber || !isValidPhoneNumber(validatedPhoneNumber)) {
        validatedPhoneNumber = '';
      }

      // Set currency and VAT rate from backend
      if (f.event?.currency) {
        setCurrency(f.event.currency);
      }
      if (f.event?.vat_rate != null) {
        setTaxPercentage(f.event.vat_rate);
      }

      setFetchedFormInfo(f);
      // Timeout to escape form.handleSubmit's scope
      // This will reset the form's internal submit state as well
      setTimeout(() => {
        safeReset(form.reset, defaultFormValues, {
          address: {
            city: f.event_account?.billing_city,
            countryCode: f.event_account?.billing_country_code,
            state: f.event_account?.billing_state,
            zip: f.event_account?.billing_zip,
            street: f.event_account?.billing_street,
            extra: f.event_account?.billing_additional_address_info ?? '',
          },
          comment: f.ops_order?.comment,
          companyName: f.event_account?.company_name,
          firstName: f.event_account?.billing_first_name,
          lastName: f.event_account?.billing_last_name,
          email: f.event_account?.billing_email,
          businessType: f.event_account?.business_type,
          dateOfBirth: f.event_account?.date_of_birth
            ? new Date(f.event_account?.date_of_birth)
            : undefined,
          phoneNumber: validatedPhoneNumber,
          vatId: f.event_account?.vat,
          corporateStructureInfo: f.event_account?.corporate_structure_info,
        });
      }, 0);

      const remoteOrder = getContainerOrderFromRemoteFormData(f);
      setOrder(remoteOrder);
      remoteOrderRef.current = remoteOrder;
      const alreadySubmitted = !!f.ops_order?.latest_revision;
      setAlreadySubmittedLock(alreadySubmitted);
      setTermsAndConditionsAccepted(alreadySubmitted);
      setTermsAndConditionsCheckbox({
        // If the event has no additional info, set the additional info checkbox to true by default to skip
        additionalInfo: !f.event?.merchant_order_form_info_text ? true : false,
        termsAndConditions: false,
      });
      setConfirmationErrors({
        additionalInfo: false,
        termsAndConditions: false,
      });
      setFormLoadingStatus('ready');
    },
    [form.reset],
  );

  const fetchAndApplyRemoteFormData = useCallback(async () => {
    if (!sharedLinkId) {
      return;
    }
    setFormLoadingStatus('loading');
    const { data, error } = await fetchRemoteFormData({
      variables: {
        merchant_order_shared_link_id: sharedLinkId,
      },
    });

    if (!data || error) {
      console.error('fetchAndApplyRemoteFormData error', error);
      setFormLoadingStatus('fetch-error');
      return;
    }

    const { eventMerchantOrderSharedLinkInfo } = data;

    if (!eventMerchantOrderSharedLinkInfo?.event_account) {
      setFormLoadingStatus('invalid-link');
      return;
    }

    applyRemoteFormData(eventMerchantOrderSharedLinkInfo);
  }, [applyRemoteFormData, fetchRemoteFormData, sharedLinkId]);

  useEffect(() => {
    if (!sharedLinkId) {
      return;
    }
    fetchAndApplyRemoteFormData();
  }, [fetchAndApplyRemoteFormData, sharedLinkId]);

  const acceptTermsAndConditions = () => {
    // Only confirmations that are on screen can block: an event without an info
    // text renders no AdditionalNote card and presets additionalInfo to true.
    const missing = {
      additionalInfo:
        !!fetchedFormInfo?.event?.merchant_order_form_info_text &&
        !termsAndConditionsCheckbox.additionalInfo,
      termsAndConditions: !termsAndConditionsCheckbox.termsAndConditions,
    };

    if (missing.additionalInfo || missing.termsAndConditions) {
      setConfirmationErrors(missing);
      // The cards render in this order, so the first missing one is the topmost.
      const topmost = missing.additionalInfo
        ? CONFIRMATION_CHECKBOX_IDS.additionalInfo
        : CONFIRMATION_CHECKBOX_IDS.termsAndConditions;
      const checkbox = document.getElementById(topmost);
      // Focus without its own jump first, then scroll smoothly exactly once.
      checkbox?.focus({ preventScroll: true });
      checkbox?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    flushSync(() => {
      setTermsAndConditionsAccepted(true);
    });
    // Scroll to the first field group
    const el = document.querySelector(
      '[data-slot="field-group"]',
    ) as HTMLElement;
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const unlockAlreadySubmittedLock = () => {
    setAlreadySubmittedLock(false);
  };

  const updateOrder = ({
    itemLocationId,
    containerTypeId,
    quantity,
  }: {
    itemLocationId: string;
    containerTypeId: number;
    quantity: number;
  }) => {
    setOrder((prev) => {
      if (!prev[itemLocationId]?.containers[containerTypeId]) {
        return prev;
      }
      const newOrder = {
        ...prev,
        [itemLocationId]: {
          ...prev[itemLocationId],
          containers: {
            ...prev[itemLocationId].containers,
            [containerTypeId]: {
              ...prev[itemLocationId].containers[containerTypeId],
              quantity,
            },
          },
        },
      };

      return newOrder;
    });
  };

  const updateItemTypeOrder = ({
    itemLocationId,
    itemTypeId,
    quantity,
  }: {
    itemLocationId: string;
    itemTypeId: number;
    quantity: number;
  }) => {
    setOrder((prev) => {
      const entry = prev[itemLocationId]?.itemTypes[itemTypeId];
      if (!entry) {
        return prev;
      }
      return {
        ...prev,
        [itemLocationId]: {
          ...prev[itemLocationId],
          itemTypes: {
            ...prev[itemLocationId].itemTypes,
            [itemTypeId]: { ...entry, quantity },
          },
        },
      };
    });
  };

  const showBillingAddressNotFound = () => {
    const message = i18n.t('EventOrder:billingAddressNotFound');
    // The inputs are disabled while the status is 'submitting', and a
    // disabled input cannot take the focus: enable them first.
    flushSync(() => {
      setErrorMessage(message);
      setFormLoadingStatus('submit-error');
    });
    BILLING_ADDRESS_FIELDS.forEach((name) =>
      form.setError(name, { type: BILLING_ADDRESS_INVALID, message }),
    );
    form.setFocus(BILLING_ADDRESS_FIELDS[0]);
  };

  // The server rejects the address as a whole, so a change to any one of its
  // fields takes the mark off all of them.
  useEffect(
    () =>
      form.subscribe({
        name: 'address',
        formState: { values: true },
        callback: () => {
          BILLING_ADDRESS_FIELDS.forEach((field) => {
            if (
              form.getFieldState(field).error?.type === BILLING_ADDRESS_INVALID
            ) {
              form.clearErrors(field);
            }
          });
        },
      }),
    [form],
  );

  const submitForm = form.handleSubmit(async () => {
    if (!sharedLinkId) {
      return;
    }
    setFormLoadingStatus('submitting');

    const orderItems: SubmitEventMerchantOrderItemInput[] = [];

    Object.values(order).forEach((itemLocation) => {
      Object.values(itemLocation.containers).forEach((container) => {
        if (container.quantity === 0) {
          return;
        }
        orderItems.push({
          item_location_id: itemLocation.itemLocationId!,
          type_id: container.info.id!,
          amount_int: container.quantity,
          unit: container.unit!,
        });
      });
      // Item type lines travel in the same list; type_id 0 marks them.
      Object.values(itemLocation.itemTypes).forEach((line) => {
        if (line.quantity === 0) {
          return;
        }
        orderItems.push({
          item_location_id: itemLocation.itemLocationId,
          type_id: 0,
          item_type_id: line.info.id,
          amount_int: line.quantity,
          unit: MerchantOrderItemUnit.Pieces,
        });
      });
    });

    try {
      const { data, errors } = await submitEventMerchantOrder({
        variables: {
          submitEventMerchantOrderInput: {
            merchant_order_shared_link_id: sharedLinkId,
            comment: form.getValues('comment'),
            event_account: {
              billing_first_name: form.getValues('firstName'),
              billing_last_name: form.getValues('lastName'),
              billing_additional_address_info: form.getValues('address.extra'),
              billing_city: form.getValues('address.city'),
              billing_country_code: form.getValues('address.countryCode'),
              billing_state: form.getValues('address.state'),
              billing_street: form.getValues('address.street'),
              billing_zip: form.getValues('address.zip'),
              company_name: form.getValues('companyName'),
              billing_email: form.getValues('email'),
              phone_number: form.getValues('phoneNumber'),
              business_type: form.getValues('businessType'),
              date_of_birth: form
                .getValues('dateOfBirth')
                ?.toISOString()
                .split('T')[0],
              vat: form.getValues('vatId'),
              corporate_structure_info: form.getValues(
                'corporateStructureInfo',
              ),
            },
            merchant_order_items: orderItems,
          },
        },
      });
      if (errors && errors.length > 0) {
        console.error('submitForm errors', errors);
        if (errors.some((e) => e.message === BILLING_ADDRESS_INVALID)) {
          showBillingAddressNotFound();
          return;
        }
        const errorMessages = errors.map((e) => {
          const reason =
            (e.extensions as any)?.exception?.reason ||
            (e.extensions as any)?.reason;
          return reason || e.message;
        });
        setErrorMessage(
          i18n.t('Common:error') + ': ' + errorMessages.join(', '),
        );
        setFormLoadingStatus('submit-error');
        return;
      }

      if (!data?.submitEventMerchantOrder?.ops_order) {
        fetchAndApplyRemoteFormData();
        return;
      }

      applyRemoteFormData(data.submitEventMerchantOrder);
    } catch (error) {
      console.error('submitForm errors', error);
      if (
        error instanceof ApolloError &&
        error.graphQLErrors.some((e) => e.message === BILLING_ADDRESS_INVALID)
      ) {
        showBillingAddressNotFound();
        return;
      }
      setErrorMessage(
        i18n.t('Common:error') +
          ': ' +
          (error instanceof Error ? error.message : String(error)),
      );
      setFormLoadingStatus('submit-error');
    }
  });

  const resetForm = () => {
    fetchAndApplyRemoteFormData();
  };

  const value: OrderFormContextValue = {
    termsAndConditionsCheckbox,
    confirmationErrors,
    setConfirmation,
    currency,
    minimumCleaningFeeRate:
      fetchedFormInfo?.event?.minimum_cleaning_fee_rate ?? null,
    formLoadingStatus,
    errorMessage,
    order,
    acceptTermsAndConditions,
    termsAndConditionsAccepted,
    submitForm,
    fetchedFormInfo,
    updateOrder,
    updateItemTypeOrder,
    hasOrderChanged,
    alreadySubmittedLock,
    unlockAlreadySubmittedLock,
    resetForm,
    formControl: form.control,
    taxPercentage,
  };

  return (
    <OrderFormContext.Provider value={value}>
      <div className="tw:flex tw:flex-col tw:flex-1">{children}</div>
      <EventOrderBottomBar.Connected />
      {process.env.NODE_ENV === 'development' && (
        <DevTool control={form.control} placement="bottom-right" />
      )}
    </OrderFormContext.Provider>
  );
}

export const useOrderFormContext = (): OrderFormContextValue => {
  const ctx = useContext(OrderFormContext);
  if (!ctx) {
    throw new Error(
      'useOrderFormContext must be used within OrderFormProvider',
    );
  }
  return ctx;
};
